import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Utils
import { openColorEyeDropper, isEyeDropperSupported } from '../../../../../../utils/color/eyedropperUtil';
import { hasNoModifierKeys } from '../../../../../../utils/keyboard/keyboardUtility';
import { getSelectedElementType } from '../colorToolUtils';
import { isPlatformPhoneOrMobileMode } from '../../../../../../platform/utils/platformDetailsUtils';
import globalLogger from '../../../../../../logger';
import { getClosestDomElement } from '../../../../../../utils/dom/domTraversalUtils';
import { getColorToPersist } from '../../../../../../../common/colors/elementColorFormatUtil';

// Actions
import { updateSelectedElementsBackgroundColors, updateSelectedElementsColors } from '../colorActions';
import { updateLabel } from '../../../../../../../common/labels/labelActions';

// Selectors
import { isCurrentlyEditing } from '../../../../../../element/selection/currentlyEditingSelector';
import { activePopupSelector } from '../../../../../../components/popupPanel/popupOpenSelector';
import { getPlatformDetailsSelector } from '../../../../../../platform/platformSelector';
import { getActiveToolbarLabel } from '../../../../../../element/labels/labelSelectors';

// Types
import { ImMNElement } from '../../../../../../../common/elements/elementModelTypes';
import { ElementType } from '../../../../../../../common/elements/elementTypes';

// Constants
import { PopupIds } from '../../../../../../components/popupPanel/popupConstants';

const logger = globalLogger.createChannel('workspace.toolbar');

const CAPTION_SUPPORTED_ELEMENTS = [
    ElementType.IMAGE_TYPE,
    ElementType.FILE_TYPE,
    ElementType.SKETCH_TYPE,
    ElementType.LINK_TYPE,
    ElementType.DOCUMENT_TYPE,
    ElementType.TABLE_TYPE,
];

// Check if the keyboard event has come from a label by using the 'closest()' function
// to traverse the element tree
const isLabelRenameEventTarget = (event: KeyboardEvent) => {
    const targetElement = getClosestDomElement(event.target);
    if (!(targetElement instanceof HTMLElement)) return false;

    return targetElement.isContentEditable && !!targetElement.closest('.LabelElementPillsContainer');
};

/**
 * Hook to handle eye dropper keyboard shortcut (i key) for color tools
 *
 * @param selectedElements - List of selected elements
 * @param supportBackgroundColor - Whether element supports background color (false = foreground color)
 * @param isPopupOpen - Current popup state (undefined = always active)
 * @param shouldApplyBackgroundColor - Whether to apply background color
 */
const useColorToolEyeDropper = (
    selectedElements: Immutable.List<ImMNElement>,
    supportBackgroundColor = false,
    isPopupOpen?: boolean,
    shouldApplyBackgroundColor?: boolean,
) => {
    const dispatch = useDispatch();
    const isCurrentlyEditingElement = useSelector(isCurrentlyEditing);
    const activePopup = useSelector(activePopupSelector);
    const platformDetails = useSelector(getPlatformDetailsSelector);
    const activeToolbarLabel = useSelector(getActiveToolbarLabel);
    const isPhoneOrMobile = isPlatformPhoneOrMobileMode(platformDetails);

    useEffect(() => {
        // Only handle when we have exactly one selected element and not currently editing
        if (selectedElements?.size !== 1 || isCurrentlyEditingElement) {
            return;
        }

        // Check if any non-color popup is open, using the matching logic from the color tool
        // as it uses uuids in its popup ids
        const hasNonColorPopup = activePopup
            .keySeq()
            .some((popupId: string) => !popupId?.toLowerCase().startsWith(PopupIds.COLOR));

        // Don't trigger eyedropper for elements that support captions UNLESS the color popup is open, or we are updating a label instead of the element
        if (
            CAPTION_SUPPORTED_ELEMENTS.includes(getSelectedElementType(selectedElements)) &&
            !isPopupOpen &&
            !activeToolbarLabel
        ) {
            return;
        }

        // Don't trigger if there's a non-color popup open
        if (hasNonColorPopup) {
            return;
        }

        const handleKeyDown = async (event: KeyboardEvent) => {
            // Check if the 'i' key is pressed without any modifiers
            if (event.key !== 'i' || !hasNoModifierKeys(event) || isPhoneOrMobile) {
                return;
            }

            if (isLabelRenameEventTarget(event)) {
                return;
            }

            // Prevent default behavior and stop propagation
            event.preventDefault();
            event.stopPropagation();

            // Check if eye dropper is supported
            if (!isEyeDropperSupported()) {
                logger.warn('EyeDropper API is not supported in this browser');
                return;
            }

            try {
                const color = await openColorEyeDropper();
                if (!color) {
                    return;
                }

                if (activeToolbarLabel) {
                    dispatch(updateLabel(activeToolbarLabel._id, { color: getColorToPersist(color) ?? undefined }));
                    return;
                }

                // Determine whether to apply background or foreground color
                const shouldApplyBackground = isPopupOpen ? shouldApplyBackgroundColor : supportBackgroundColor;

                if (shouldApplyBackground) {
                    dispatch(updateSelectedElementsBackgroundColors(color, null));
                } else {
                    dispatch(
                        updateSelectedElementsColors({
                            color,
                            useExistingColorSpace: false,
                            secondaryColor: null,
                        }),
                    );
                }
            } catch (error) {
                logger.error('Failed to use eyeDropper', error);
            }
        };

        // Add the event listener with capture to ensure we get the event before other listeners
        document.addEventListener('keydown', handleKeyDown, { capture: true });

        // Cleanup function to remove the event listener
        return () => {
            document.removeEventListener('keydown', handleKeyDown, { capture: true });
        };
    }, [
        selectedElements,
        supportBackgroundColor,
        isPopupOpen,
        shouldApplyBackgroundColor,
        isCurrentlyEditingElement,
        activePopup,
        activeToolbarLabel?._id,
    ]);
};

export default useColorToolEyeDropper;
